import React from "react";
import { twMerge } from "tailwind-merge";
import { Link } from "react-router-dom";
import type { Placement } from "@floating-ui/react";
import { Avatar } from "@themes/pollinator/ui/Avatar";
import { Button } from "@themes/pollinator/ui/Button";
import { Dropdown } from "@themes/pollinator/ui/Dropdown";
import { Type } from "@themes/pollinator/ui/Type";

export type Props = {
  /** Additional CSS classes */
  className?: string;
  /** User photo URL */
  photo?: string;
  /** User full name */
  fullName: string;
  /** Whether user data is loaded */
  isLoaded: boolean;
  /** Navigation click handler */
  onNavigationClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => void;
  /** Menu items to display */
  menuItems: {
    title: string;
    href: string;
  }[];
  /** Dropdown placement relative to trigger (defaults to "bottom") */
  placement?: Placement;
  /** Show name next to avatar in trigger on all screen sizes (defaults to mobile only) */
  showNameInTrigger?: boolean;
  /** Additional CSS classes for the trigger wrapper */
  triggerClassName?: string;
  /** Whether the dropdown is in an active state (e.g., current route matches) */
  isActive?: boolean;
};

/**
 * Platform avatar dropdown with user info and navigation menu.
 */
export function PlatformAvatarDropdown({
  className,
  photo,
  fullName,
  isLoaded = false,
  onNavigationClick,
  menuItems,
  placement,
  showNameInTrigger = false,
  triggerClassName,
  isActive = false,
}: Props) {
  return (
    <Dropdown
      placement={placement}
      triggerClassName={triggerClassName}
      trigger={
        <button
          className={twMerge(
            "flex items-center with-ring focus:rounded-lg",
            className,
            isActive && "bg-brand-100"
          )}
          data-testid="platform-avatar-dropdown-trigger"
        >
          <Avatar
            image={photo}
            size={36}
            alt={fullName}
            isLoading={!isLoaded}
            className="cursor-pointer hover:bg-brand-900 text-brand-50"
          />
          <Type variant="h5" className={twMerge("ml-4", !showNameInTrigger && "md:hidden")}>
            {fullName}
          </Type>
        </button>
      }
    >
      <div
        className="flex items-center px-4 py-4 border-b"
        data-testid="platform-avatar-dropdown-header"
      >
        <Avatar
          image={photo}
          size={36}
          alt={fullName}
          isLoading={!isLoaded}
          className="cursor-pointer hover:bg-brand-900 text-brand-50"
        />
        <Type variant="h5" className="ml-4">
          {fullName}
        </Type>
      </div>

      <div className="flex flex-col gap-0" data-testid="platform-avatar-dropdown-menu">
        {menuItems.map((item) => (
          <Button
            key={item.title}
            as={Link}
            to={item.href}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, item.href)
            }
            variant="menu-item"
            className="justify-start w-full"
            data-testid={`dashboard-nav-${item.title}`}
            title={item.title}
            aria-label={item.title}
          >
            {item.title}
          </Button>
        ))}
      </div>
    </Dropdown>
  );
}

export default PlatformAvatarDropdown;

